import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";

import Navbar from "../../../Layouts/Navbar/Navbar";
import fileFolderConfig from "../../../commonFunctions/fileFolderConfig";
import LeftSideBar, { FolderCollapse } from "../../../components/SideBar/LeftSideBar";
import { useModalContext } from "../../../context/ModalContext";
import { useNavigationContext } from "../../../context/NavigationContext";
import { useTemplateContext } from "../../../context/TemplateContext";
import { useTreeContext } from "../../../context/TreeDataContext";
import BuilderModal from "../../../Layouts/Modal/BuilderModal";
import Input from "../../../components/FormComponents/Input";
import SingleSelectBox from "../../../components/FormComponents/SingleSelectBox";
import Button from "../../../components/FormComponents/Button";
import ResizableComponent from "../../../Layouts/ResizableComponent";
import HeaderModes from "../../../Layouts/Navbar/HeaderModes";
import { toastError, toastSuccess } from "../../../utils/toastUtils";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import { deleteFolderOfFile, editFolderOrFile, insertNewFileOrFolder } from "../../../utils/treeData/folderEditorUtils";
import ErrorMessage from "../../../components/FormComponents/ErrorMessage";

const SqlTemplateSection = () => {
  const [isLoader, setIsLoader] = useState(false);
  const [createTemplateModalData, setCreateTemplateModalData] = useState(null);
  const [isError, setIsError] = useState(false);
  const { sqlTemplateExplorerData, setSqlTemplateExplorerData } = useTreeContext();
  const { selectedTemplate, setSelectedTemplate, fetchSqlTemplates } = useTemplateContext();
  const { getUserConfirmationForDelete } = useModalContext();
  const { saveChangesAndNavigate } = useNavigationContext();
  const navigate = useNavigate();
  const { sqlTemplateId } = useParams();

  const createTemplate = async (folder, config) => {
    try {
      const response = await axiosInstance.post(`/${folder.parentData}`, config);
      const { data, message } = response;

      const newItem = fileFolderConfig.getFileConfig(data.id, data.name, folder.parentData);
      const newTree = insertNewFileOrFolder(sqlTemplateExplorerData, folder, newItem);
      setCreateTemplateModalData(null);
      setSqlTemplateExplorerData(newTree);
      navigate(`./${data.id}`);
      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleNewInsert = async (folder, name) => {
    if (folder.insert_type === "popup") {
      setCreateTemplateModalData(folder);
      return;
    }
    if (name.trim().length < 3) {
      toastError("Name must be at least 3 characters");
      return;
    }

    const config = { name: name };
    await createTemplate(folder, config);
  };

  const handleEdit = async (folder, newName) => {
    if (newName.trim().length < 3) {
      toastError("Name must be at least 3 characters");
      return;
    }

    try {
      const response = await axiosInstance.patch(`/${folder.parentData}/${folder.id}`, { name: newName });
      const { message } = response;
      const newTree = editFolderOrFile(sqlTemplateExplorerData, folder, newName);
      setSqlTemplateExplorerData(newTree);

      if (folder.id === selectedTemplate?.id) {
        setSelectedTemplate({ ...selectedTemplate, name: newName });
      }

      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDelete = async (folder) => {
    const userConfirmed = await getUserConfirmationForDelete(folder.name);
    if (!userConfirmed) return;

    try {
      const response = await axiosInstance.delete(`/${folder.parentData}/${folder.id}`);
      const { message } = response;

      const newTree = deleteFolderOfFile(sqlTemplateExplorerData, folder);
      setSqlTemplateExplorerData(newTree);

      if (folder.id === selectedTemplate?.id) {
        navigate("./");
      }

      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const callbackFunction = (id) => {
    if (selectedTemplate.id === id) return;
    saveChangesAndNavigate(id);
  };

  const initialFetching = async () => {
    setIsLoader(true);
    setIsError(false);
    try {
      await fetchSqlTemplates();
      if (selectedTemplate.id && !sqlTemplateId) {
        setSelectedTemplate({ id: "" });
      }
      setIsLoader(false);
    } catch (error) {
      setIsError(true);
    }
  };

  useEffect(() => {
    initialFetching();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <section className="main_section">
        <div className="sub_section">
          <ResizableComponent
            resizeSide="right"
            storageKey="left_sidebar_size"
            defaultSize={244}
            range={[244, 400]} // Specify the range as [minSize, maxSize]
            collapsible={true}
          >
            <HeaderModes />
            <div className="d_flex">
              <Navbar />
              {isError ? (
                <div className="d_flex align_center justify_center flex_column">
                  <ErrorMessage className={"text_center mb_3"}>Failed to load templates. Please try again</ErrorMessage>
                  <Button onClick={initialFetching}>Retry</Button>
                </div>
              ) : (
                <LeftSideBar
                  explorerData={sqlTemplateExplorerData}
                  setExplorerData={setSqlTemplateExplorerData}
                  selectedScreen={selectedTemplate}
                  callbackFunction={callbackFunction}
                  handleNewInsert={handleNewInsert}
                  handleDelete={handleDelete}
                  handleEdit={handleEdit}
                  type="RDBMS Templates"
                  isLoader={isLoader}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <FolderCollapse
                      explorerData={sqlTemplateExplorerData}
                      setExplorerData={setSqlTemplateExplorerData}
                      type="template"
                    />
                  </div>
                </LeftSideBar>
              )}
            </div>
          </ResizableComponent>
          <Outlet />
        </div>
        <BuilderModal
          isOpen={createTemplateModalData}
          onClose={() => setCreateTemplateModalData(null)}
          modalName="Create Database Template"
        >
          <CreateDatabaseTemplate create={(data) => createTemplate(createTemplateModalData, data)} />
        </BuilderModal>
      </section>
    </>
  );
};

const CreateDatabaseTemplate = ({ create }) => {
  const [templateConfig, setTemplateConfig] = useState({
    name: "",
    type: "rdbms"
  });

  const handleChangeTemplateConfig = (name, value) => {
    setTemplateConfig({ ...templateConfig, [name]: value });
  };

  const createTemplate = () => {
    if (templateConfig.name.trim().length < 3) {
      toastError("Name must be at least 3 characters");
      return;
    }
    create(templateConfig);
  };

  const databaseTypeOptions = [
    { id: "rdbms", name: "RDBMS" },
    { id: "postgres", name: "PostgreSQL" }
  ];

  return (
    <div className="builder_form">
      <Input
        label="Enter Database Template Name"
        name="name"
        value={templateConfig.name}
        onChange={(e) => handleChangeTemplateConfig(e.target.name, e.target.value)}
      />

      <SingleSelectBox
        selectedData={templateConfig.type}
        options={databaseTypeOptions}
        onChange={(obj) => handleChangeTemplateConfig("type", obj.id)}
        placeholder="Select Database Type"
        label="Enter Database Type"
      />

      <Button onClick={createTemplate}>Create</Button>
    </div>
  );
};

export default SqlTemplateSection;
